import { getAppInsights } from './authTelemetry';

type Props = Record<string, string | number | boolean>;

// Fire-and-forget helper for the handful of catch-blocks in the SPA that
// legitimately swallow their error — recovery paths, popup cleanup, the
// second-order failures of a primary operation that already failed. The
// audit's Patterns roll-up flagged the pattern: each swallow is defensible
// on its own, but the aggregate hides silent second-order failures that
// would otherwise only surface as "user says it didn't work".
//
// What this does:
//   1. Mirror to the browser console as a `warn` so the error is visible
//      in local dev + in DevTools when debugging against prod.
//   2. Best-effort `trackException` through the shared App Insights SDK.
//      Async / fire-and-forget — callers stay synchronous.
//
// What this does NOT do:
//   - Throw. Ever. A telemetry helper that can itself throw would defeat
//     the point of wrapping a recovery path.
//   - Block the caller. Consumers call into `reportNonFatal(...)` and
//     continue immediately. The AI beacon fires on the microtask queue.
export function reportNonFatal(err: unknown, context: string, properties?: Props): void {
  const exception = err instanceof Error ? err : new Error(String(err));

  try {
    // Console mirror is always attempted first — cheap, works even when
    // App Insights init failed or is missing a connection string (dev).
    console.warn(`[non-fatal] ${context}`, exception, properties ?? {});
  } catch {
    // If even console.warn throws (hard to imagine but not impossible in
    // some embedded hosts), fall through to the AI branch.
  }

  void (async () => {
    try {
      const ai = await getAppInsights();
      if (!ai) return; // Init failed or no conn string (dev build).
      ai.trackException({
        exception,
        properties: { context, ...stringifyProps(properties) },
      });
    } catch {
      // Never let our telemetry helper propagate an error to the caller —
      // the whole point is that the caller has already chosen to swallow.
    }
  })();
}

// trackException's properties bag is typed as Record<string, string>; coerce
// numbers / booleans to strings to keep the caller API permissive.
function stringifyProps(props: Props | undefined): Record<string, string> {
  if (!props) return {};
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(props)) {
    out[k] = String(v);
  }
  return out;
}
